import { useState, type FormEvent } from 'react';
import type { Device, DeviceRegistration } from '@/api/endpointsInstall';
import { CopyButton } from '@/components/CopyButton';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { ProblemAlert } from '@/components/ProblemAlert';
import { QrCode } from '@/components/QrCode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useNow } from '@/lib/useNow';
import { expiryText } from '@/features/builds/detail/installModel';
import { appleStatusOf, registrationState } from './devicesModel';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Creates the waiting row and its link (`POST /devices/registrations`). */
  onCreate: (name: string) => Promise<DeviceRegistration>;
  creating: boolean;
  error: unknown;
  /** The org's devices as the page refreshes them, to follow the row this sheet created. */
  devices: Device[] | undefined;
}

/**
 * Register an iOS device (SPEC decision 28): an optional name, then a QR code of the registration page (15 minutes). The
 * iPhone opens it in Safari, installs the configuration profile and Hangar receives its UDID; the sheet follows the row
 * until it is registered.
 */
export function RegisterDeviceSheet({ open, onOpenChange, onCreate, creating, error, devices }: Props) {
  const [name, setName] = useState('');
  const [created, setCreated] = useState<DeviceRegistration | null>(null);
  // The page remounts this sheet (a new `key`) each time it opens, so closing keeps the last state through the exit motion.
  const close = (next: boolean) => onOpenChange(next);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try { setCreated(await onCreate(name.trim())); } catch { /* shown by ProblemAlert */ }
  };
  return (
    <Sheet open={open} onOpenChange={close}>
      <SheetContent side="right" data-testid="register-device-sheet" className="w-full gap-0 p-0 sm:max-w-md" aria-describedby="register-device-desc">
        <SheetHeader>
          <SheetTitle>Register an iOS device</SheetTitle>
          <SheetDescription id="register-device-desc" className="text-xs">Internal iOS builds install only on registered iPhones and iPads.</SheetDescription>
        </SheetHeader>
        {created ? (
          <Waiting registration={created} device={devices?.find((d) => d.id === created.device.id)} onDone={() => close(false)} />
        ) : (
          <form onSubmit={(e) => void submit(e)} className="flex min-h-0 flex-1 flex-col" noValidate>
            <div className="flex flex-col gap-4 p-4 sm:p-5">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="device-name">Name (optional)</Label>
                <Input id="device-name" value={name} maxLength={80} placeholder="Design team iPhone" onChange={(e) => setName(e.target.value)} data-testid="register-device-name" />
                <p className="text-xs text-subtle">Without a name, Hangar uses the device's model.</p>
              </div>
              <ProblemAlert error={error} testId="register-device-problem" />
            </div>
            <SheetFooter className="flex-row justify-end border-t">
              <Button type="button" variant="ghost" onClick={() => close(false)}>Cancel</Button>
              <Button type="submit" loading={creating} data-testid="register-device-create">
                {creating ? null : <Icon name="qr_code_2" size={18} />}
                Create registration code
              </Button>
            </SheetFooter>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Waiting({ registration, device, onDone }: { registration: DeviceRegistration; device: Device | undefined; onDone: () => void }) {
  const now = useNow(5_000);
  const state = registrationState(device ?? registration.device, now);
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4 sm:p-5">
      {state === 'registered' && device ? <Registered device={device} /> : (
        <section className="enter flex flex-col items-center gap-3" aria-label="QR code">
          <div className="rounded-xl border bg-white p-3 shadow-card">
            <QrCode value={registration.url} label="QR code of the device registration page" testId="register-device-qr" className={state === 'expired' ? 'size-56 opacity-20' : 'size-56'} />
          </div>
          <p className="flex items-center gap-2 text-center text-sm text-muted-foreground" data-testid="register-device-state" data-state={state}>
            {state === 'waiting' ? <Icon name="pending" size={16} className="text-awaiting" /> : null}
            {state === 'waiting' ? 'Waiting for the device…' : 'This code expired. Close and create a new one.'}
          </p>
        </section>
      )}
      {state !== 'registered' ? (
        <>
          <section className="flex flex-col gap-2" aria-label="Send link">
            <Kicker>Send link</Kicker>
            <div className="flex items-center gap-2">
              <Input readOnly value={registration.url} aria-label="Registration link" data-testid="register-device-link" className="min-w-0 font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
              <CopyButton text={registration.url} label="Copy" testId="register-device-copy" />
            </div>
            <p className="text-xs text-subtle">{expiryText(registration.expiresAt, now)}</p>
          </section>
          <section className="flex flex-col gap-2" aria-label="On the iPhone or iPad">
            <Kicker>On the iPhone or iPad</Kicker>
            <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-sm text-muted-foreground">
              <li>Scan the code with the camera and open the page in Safari.</li>
              <li>Tap Download profile, then Allow.</li>
              <li>Open Settings → Profile Downloaded → Install. iOS shows the profile as Not Verified: that is expected.</li>
            </ol>
          </section>
        </>
      ) : null}
      <SheetFooter className="mt-auto flex-row justify-end p-0">
        <Button variant={state === 'registered' ? 'default' : 'outline'} onClick={onDone} data-testid="register-device-done">Done</Button>
      </SheetFooter>
    </div>
  );
}

function Registered({ device }: { device: Device }) {
  const apple = appleStatusOf(device);
  return (
    <section className="enter flex flex-col items-center gap-2 py-6 text-center" data-testid="register-device-registered">
      <span className="flex size-12 items-center justify-center rounded-full bg-secondary text-succeeded"><Icon name="check_circle" size={28} fill /></span>
      <h3 className="text-base font-semibold text-foreground">{device.name} is registered</h3>
      <p className="max-w-[40ch] text-sm text-muted-foreground">{apple.hint}</p>
    </section>
  );
}
